<script setup lang="ts">
import { computed, ref, watch, toRefs } from "vue";
import { useVerifyEmail } from "./useVerifyEmail.composable";
import StatusMessagePage from "@/app/registration-app/shared/components/StatusMessagePage/StatusMessagePage.vue";
import PasswordForm from "@/app/registration-app/user/verify-email/components/PasswordForm/PasswordForm.vue";
import ColumnLayout from "@/shared/components/ColumnLayout/ColumnLayout.vue";
import ColumnLayoutBox from "@/shared/components/ColumnLayoutBox/ColumnLayoutBox.vue";
import Icon from "@/shared/components/Icon/Icon.vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ email?: string; token?: string }>();
const { t } = useI18n();

const { email, token } = toRefs(props);

// --- Status Message ---
const showStatusMessage = ref(false);
const statusMessageTitle = ref("");
const statusMessageText = ref<string | undefined>(undefined);
const statusMessageSubText = ref<string | undefined>(undefined);

const setStatusMessage = (title: string, text?: string, subText?: string) => {
  statusMessageTitle.value = title;
  statusMessageText.value = text;
  statusMessageSubText.value = subText;
};

// --- Verify Email ---
const {
  isVerifyingEmail,
  verifyingEmailError,
  settingPasswordError,
  onVerifyEmail,
} = useVerifyEmail({
  email,
  token,
});

// --- Error Handling ---
const hasEmailError = computed(
  () => verifyingEmailError.value || !email.value || !token.value,
);

watch(
  [hasEmailError, settingPasswordError],
  ([hasEmailError, settingPasswordError]) => {
    if (hasEmailError) {
      setStatusMessage(
        t("registration.user.verifyEmail.error.invalidLink.title"),
        t("registration.user.verifyEmail.error.invalidLink.info"),
      );
      showStatusMessage.value = true;
    } else if (settingPasswordError) {
      setStatusMessage(
        t("registration.user.verifyEmail.error.settingPassword.title"),
        t("registration.user.verifyEmail.error.settingPassword.info"),
        t("registration.user.verifyEmail.error.settingPassword.info2"),
      );
      showStatusMessage.value = true;
    }
  },
);
</script>

<template>
  <StatusMessagePage
    v-if="showStatusMessage"
    :title="statusMessageTitle"
    :text="statusMessageText"
  >
    <template #icon>
      <Icon
        v-if="!hasEmailError"
        :size="64"
        icon="check"
        class="verify-email-view--icon"
      />
    </template>
  </StatusMessagePage>
  <ColumnLayout v-else>
    <template #column1>
      <ColumnLayoutBox>
        <PasswordForm
          :is-loading="isVerifyingEmail"
          @set-password="onVerifyEmail"
        />
      </ColumnLayoutBox>
    </template>
  </ColumnLayout>
</template>

<style scoped>
@import "VerifyEmailView.css";
</style>
